import React from 'react'
import Box from '@material-ui/core/Box'
import IconButton from '@material-ui/core/IconButton'
import HowToRegOutlinedIcon from 'components/material-symbol/icons/HowToReg'
import Typography from '@material-ui/core/Typography'
import Container from '@material-ui/core/Container'
import { makeStyles } from '@material-ui/styles'
import CheckOutlinedIcon from 'components/material-symbol/icons/Check'
import { GET_INVITATION_INFO, useAcceptSponsorship } from './queries'
import { useQuery } from '@apollo/client'
import LinearProgress from '@material-ui/core/LinearProgress'
import Error from 'components/error'
import { useLocation } from 'react-router-dom'
import { InvalidInvitation } from 'pages/jrl-signup/invalid-invitation'
import logo from 'images/pga-logo-navy.png'
import { useSnackbar, useSnackbarError, useSnackbarSuccess } from 'lib/snackbar'
import LoadingButton from 'components/loading-button'
import RouterLinkButton from 'components/router-link-button'
import { roles, withProtectedRoute } from 'lib/auth'

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: '800px',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(12),
    borderRadius: theme.spacing(1),
    border: '1px solid #E0E0E0',
    [theme.breakpoints.only('xs')]: {
      border: 'unset',
      padding: theme.spacing(1),
    },
  },
  icon: {
    background: 'rgba(0, 35, 75, 0.08)',
    padding: '20px',
    marginBottom: '12px',
  },
  text: {
    marginBottom: theme.spacing(3),
  },
}))

const AcceptSponsorship = () => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const { search } = useLocation()
  const tokenId = new URLSearchParams(search).get('token')
  const [
    callAcceptSponsorship,
    { loading: loadingAccept },
  ] = useAcceptSponsorship({ tokenId })

  const handleAcceptSponsorship = async () => {
    try {
      const { data } = await callAcceptSponsorship(tokenId)
      const { success, message } = data?.acceptInvitation
      if (!success) {
        return snackbarError(message || 'Unable to accept sponsorship')
      }
      if (success) {
        snackbarSuccess('Your PGA Coach sponsorship has been accepted')
        window.location.href = '/pga-coach'
      }
    } catch (e) {
      snackbarError(e?.message)
    }
  }
  const { data, loading, error } = useQuery(GET_INVITATION_INFO, {
    variables: { id: tokenId },
  })

  if (loading) return <LinearProgress />
  if (error) return <Error error={error} />

  const memberName = data?.invitation?.invitedBy?.name
  const canAcceptInvite = data?.invitation?.canAccept

  if (tokenId && !canAcceptInvite) {
    return <InvalidInvitation caption={memberName} invitationLogo={logo} />
  }

  return (
    <Container className={classes.container}>
      <Box className={classes.infoBlock}>
        <IconButton className={classes.icon}>
          <HowToRegOutlinedIcon style={{ color: '#00234B' }} fontSize="large" />
        </IconButton>
        <Typography variant="h6" style={{ marginBottom: '12px' }}>
          Accept Your Sponsorship!
        </Typography>
        <Typography variant="body1" align="center" className={classes.text}>
          {`Your sponsorship from ${memberName} grants you access to the PGA
            Coach platform. PGA Coach is a valuable resource that brings
            advanced tools, techniques, and professional insights right to your
            fingertips, helping you enhance your skills and knowledge in the
            world of golf.`}
        </Typography>
        <Typography variant="body1" align="center" className={classes.text}>
          By using this platform, you agree to adhere to the PGA Coach User
          Policies.
        </Typography>
        <LoadingButton
          variant="contained"
          color="primary"
          size="large"
          startIcon={<CheckOutlinedIcon />}
          onClick={() => handleAcceptSponsorship()}
          style={{ marginBottom: '12px' }}
          loading={loadingAccept}
        >
          Accept Sponsorship
        </LoadingButton>
        <RouterLinkButton variant="text" color="primary" to="/pga-coach">
          Reject Sponsorship
        </RouterLinkButton>
      </Box>
    </Container>
  )
}

export default withProtectedRoute(AcceptSponsorship, roles.COACH)
